/**
 * SSE 客户端封装
 *
 * 监听后端 6 种 SSE 事件：token / thinking / file / workflow_trace / done / error
 */

import type {
  FileInfo,
  ThinkingItem,
  WorkflowNodeEvent,
  FinalMessagePayload
} from '../types/contentBlock';

// ─── 回调接口 ─────────────────────

export interface SSECallbacks {
  /** 收到文本增量 */
  readonly onToken?: (delta: string, fullContent: string) => void;
  /** 收到思考/推理增量 */
  readonly onThinking?: (delta: string, thinkingId: string, tool?: string) => void;
  /** 收到文件 */
  readonly onFile?: (file: FileInfo) => void;
  /** 收到工作流追踪事件 */
  readonly onWorkflowTrace?: (trace: WorkflowNodeEvent) => void;
  /** 流结束 */
  readonly onDone?: (data: {
    conversationId?: string;
    totalLength: number;
    message?: FinalMessagePayload;
  }) => void;
  /** 错误 */
  readonly onError?: (error: Error) => void;
  /** 连接打开 */
  readonly onOpen?: () => void;
}

export interface SSEClientOptions extends SSECallbacks {
  /** SSE 端点 URL */
  readonly url: string;
}

export interface SSEClient {
  /** 关闭连接 */
  close: () => void;
  /** 连接是否已关闭 */
  readonly isClosed: boolean;
}

/**
 * 创建 SSE 客户端连接
 */
export const createSSEClient = (options: SSEClientOptions): SSEClient => {
  const {
    url,
    onToken,
    onThinking,
    onFile,
    onWorkflowTrace,
    onDone,
    onError,
    onOpen
  } = options;

  let closed = false;
  let eventSource: EventSource | null = null;

  try {
    eventSource = new EventSource(url);

    eventSource.onopen = () => {
      if (!closed) {
        onOpen?.();
      }
    };

    // ★ token 事件
    eventSource.addEventListener('token', (event: MessageEvent) => {
      if (closed) return;
      try {
        const data = JSON.parse(event.data) as { delta: string; fullContent: string };
        onToken?.(data.delta, data.fullContent);
      } catch (err) {
        console.error('[SSEClient] Failed to parse token:', err);
      }
    });

    // ★ thinking 事件
    eventSource.addEventListener('thinking', (event: MessageEvent) => {
      if (closed) return;
      try {
        const data = JSON.parse(event.data) as { delta: string; thinkingId: string; tool?: string };
        onThinking?.(data.delta, data.thinkingId, data.tool);
      } catch (err) {
        console.error('[SSEClient] Failed to parse thinking:', err);
      }
    });

    // ★ file 事件
    eventSource.addEventListener('file', (event: MessageEvent) => {
      if (closed) return;
      try {
        const file = JSON.parse(event.data) as FileInfo;
        onFile?.(file);
      } catch (err) {
        console.error('[SSEClient] Failed to parse file:', err);
      }
    });

    // ★ workflow_trace 事件
    eventSource.addEventListener('workflow_trace', (event: MessageEvent) => {
      if (closed) return;
      try {
        const trace = JSON.parse(event.data) as WorkflowNodeEvent;
        onWorkflowTrace?.(trace);
      } catch (err) {
        console.error('[SSEClient] Failed to parse workflow_trace:', err);
      }
    });

    // ★ done 事件
    eventSource.addEventListener('done', (event: MessageEvent) => {
      if (closed) return;
      try {
        const data = JSON.parse(event.data) as {
          conversationId?: string;
          totalLength: number;
          message?: FinalMessagePayload;
        };
        onDone?.(data);
      } catch (err) {
        console.error('[SSEClient] Failed to parse done:', err);
      }
      eventSource?.close();
      closed = true;
    });

    // ★ error 事件（业务错误）
    eventSource.addEventListener('error', (event: MessageEvent) => {
      if (closed) return;
      try {
        const data = JSON.parse(event.data) as { message: string };
        onError?.(new Error(data.message));
      } catch {
        onError?.(new Error('Unknown SSE error'));
      }
      eventSource?.close();
      closed = true;
    });

    // 连接级别错误
    eventSource.onerror = () => {
      if (closed) return;
      onError?.(new Error('SSE 连接断开'));
      eventSource?.close();
      closed = true;
    };

  } catch (err) {
    closed = true;
    onError?.(err instanceof Error ? err : new Error(String(err)));
  }

  return {
    close: () => {
      if (!closed) {
        closed = true;
        eventSource?.close();
      }
    },
    get isClosed() {
      return closed;
    }
  };
};